/**
 * The Art of Story — Marlow, the rehearsal partner.
 *
 * Ported from Profound Thinking's Clara page (itself descended from Profound
 * Questions' Quinn and Profound Living's Willow, the reference coaching
 * client) and re-cut for Marlow's register and this app's palette. Structure:
 * intro → sign-in (or consent) → conversation, with the thread carried across
 * navigation and reloads via sessionStorage.
 *
 * The intro leads even when signed out — the family rule is that nobody is
 * asked for anything before they know who is asking and why.
 *
 * Australian English throughout.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Download, Trash2, LogIn, HelpCircle, RotateCcw, Settings as SettingsIcon, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import companionService from '../services/companionService';
import { resolveCompanionAccess } from '../services/companionAccess';
import {
  CrisisResourcesCard,
  ProfessionalSupportCard,
  SubtleProfessionalHint,
  CompanionDisclaimer,
  UsageIndicator,
  ConsentGate,
} from '../components/companion/CompanionPanels';
import { renderMarkdown } from '../components/companion/renderMarkdown.jsx';
import MarlowIntro from '../components/companion/MarlowIntro';
import MarlowAvatar from '../components/companion/MarlowAvatar';
import { saveModuleContext, loadModuleContext, clearModuleContext } from '../services/companionModuleContext';

// Four openers spanning Marlow's range — find the story, fix a flat one,
// prepare for a specific occasion, and learn to listen. Each is a moment
// rather than a topic, which is exactly what she asks people to bring.
const suggestedPrompts = [
  'Something happened to me last week that I keep thinking about. Help me find the story in it.',
  'I tell a story that always falls a bit flat. Help me work out why.',
  'I have to speak at something on the weekend. Help me build what I’ll say.',
  'Teach me how to draw a story out of someone else without taking it over.',
];

// sessionStorage, not localStorage, and deliberately: a conversation that
// walks through a hard thing in your life shouldn't outlive the browser tab on
// a shared machine. The durable copy is the transcript the Hub keeps against
// the account, which can be exported or deleted from the "What Marlow keeps"
// panel.
const THREAD_KEY = 'aos_marlow_thread_v1';
const THREAD_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const readThread = (uid) => {
  if (!uid) return null;
  const key = `${THREAD_KEY}:${uid}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    const usable =
      Array.isArray(saved?.messages) &&
      saved.messages.length > 0 &&
      saved.savedAt &&
      Date.now() - saved.savedAt <= THREAD_MAX_AGE_MS;
    if (!usable) { sessionStorage.removeItem(key); return null; }
    return saved;
  } catch {
    try { sessionStorage.removeItem(key); } catch { /* nothing to do */ }
    return null;
  }
};

const writeThread = (uid, payload) => {
  if (!uid) return;
  try {
    sessionStorage.setItem(`${THREAD_KEY}:${uid}`, JSON.stringify({ ...payload, savedAt: Date.now() }));
  } catch {
    /* private mode / quota — the conversation still works, it just won't resume */
  }
};

// When the teller arrives from a module, the openers are about applying THAT
// module to something real rather than the generic four.
const modulePrompts = (title) => [
  `I've just finished ${title}. Help me use it on a story I actually want to tell.`,
  `What do most people get wrong when they try to apply ${title}?`,
  `Listen to a story of mine and tell me what ${title} says about how I'm telling it.`,
];

const clearThread = (uid) => {
  if (!uid) return;
  try { sessionStorage.removeItem(`${THREAD_KEY}:${uid}`); } catch { /* nothing to do */ }
};

export default function CompanionPage() {
  const location = useLocation();
  // Arrived from a module (the menu link while reading one notes it): open on
  // that module rather than a blank box, and send it with every message so
  // Marlow knows what the teller was reading. Kept in sessionStorage so it
  // survives the sign-in redirect; "Talk about something else" and a new
  // conversation drop it.
  const [fromModule, setFromModule] = useState(() => {
    const fromState = location.state?.fromModule || null;
    return fromState ? saveModuleContext(fromState) : loadModuleContext();
  });
  const dropModuleContext = () => {
    clearModuleContext();
    setFromModule(null);
  };
  const { isAuthenticated, loading: authLoading, redirectToAuth, auth } = useAuth();
  const { assessmentResults } = useApp();

  // The gate is the Firebase session itself, not `isAuthenticated` — see
  // companionAccess.js for why, and its test for the cases it pins.
  const { canTalk, staleLocalSession, uid } = resolveCompanionAccess({
    authLoading,
    isAuthenticated,
    firebaseUser: auth?.currentUser || null,
  });

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [crisis, setCrisis] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  // The intro comes first for anyone who hasn't consented yet — nobody should
  // be asked for their name and country before they know who is asking.
  const [introDone, setIntroDone] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  // "What Marlow keeps" — export and delete controls, keeping the consent
  // gate's read-export-delete promise honest without a separate route.
  const [showMemory, setShowMemory] = useState(false);
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [memoryNote, setMemoryNote] = useState(null);
  // Messages restored from a previous visit render without an entrance
  // animation — framer entrance states are the standing trap in this family.
  const [restoredCount, setRestoredCount] = useState(0);
  const bottomRef = useRef(null);

  // Don't ask the Hub for a profile until there's a real token to ask with.
  useEffect(() => {
    if (authLoading) return;
    if (!canTalk) { setLoadingProfile(false); return; }
    (async () => {
      const res = await companionService.getProfile();
      if (res?.success) setProfile(res.profile);
      setLoadingProfile(false);
    })();
  }, [canTalk, authLoading]);

  // Pick the thread back up, across in-app navigation and across a reload.
  const hydrated = useRef(false);
  useEffect(() => {
    if (!uid) return;
    const saved = readThread(uid);
    hydrated.current = true;
    if (!saved) return;
    companionService.restore({
      sessionId: saved.sessionId,
      conversationHistory: saved.conversationHistory,
    });
    setMessages(saved.messages);
    setRestoredCount(saved.messages.length);
    setSessionStarted(Boolean(saved.sessionId));
  }, [uid]);

  // Save after every exchange, including the service's own session id and
  // history. Gated on `hydrated` so the mount render can't clear the very
  // thread that was about to be restored.
  useEffect(() => {
    if (!uid || !hydrated.current) return;
    if (!messages.length) { clearThread(uid); return; }
    writeThread(uid, { messages, ...companionService.snapshot() });
  }, [uid, messages]);

  const startNewConversation = async () => {
    await companionService.endSession().catch(() => {});
    companionService.resetSession();
    clearThread(uid);
    setMessages([]);
    setRestoredCount(0);
    setCrisis(null);
    setProfessional(null);
    setError(null);
    setSessionStarted(false);
    dropModuleContext();
  };

  // Only chase the bottom once a conversation exists, and stop at the end of
  // the messages rather than the end of the page (the composer is fixed).
  useEffect(() => {
    if (!messages.length && !crisis && !professional) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, crisis, professional]);

  const handleGrantConsent = async (opts) => {
    const res = await companionService.grantConsent(opts);
    if (res?.success) setProfile(res.profile);
    return res;
  };

  const ensureSession = async () => {
    if (sessionStarted) return;
    await companionService.startSession({ sessionType: 'exploration' });
    setSessionStarted(true);
  };

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    await ensureSession();
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');

    // The in-memory assessment result is preferred over the localStorage copy:
    // a teller who has just finished the profile in this session has results
    // in context before the storage write has necessarily landed.
    const res = await companionService.askCoach(trimmed, {
      storyProfile: assessmentResults || null,
      module: fromModule,
    });
    if (res?.success) {
      setMessages((prev) => [...prev, { role: 'assistant', content: res.response }]);
      setCrisis(res.crisisResources || null);
      setProfessional(res.professionalSupport || null);
      setUsage(res.usage || null);
    } else {
      setError(res?.response || 'Something went wrong.');
      if (res?.dailyCapExceeded) {
        setUsage({
          tier: res.tier, dailyLimit: res.dailyLimit, used: res.dailyLimit,
          remaining: 0, upgradeUrl: res.upgradeUrl,
        });
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: res?.response || '…', isError: true }]);
    }
    setSending(false);
  };

  const exportMemory = async () => {
    setMemoryBusy(true);
    setMemoryNote(null);
    const res = await companionService.exportData();
    if (res?.success !== false) {
      try {
        const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'marlow-export.json';
        a.click();
        URL.revokeObjectURL(url);
        setMemoryNote('Exported. Check your downloads.');
      } catch {
        setMemoryNote('The export arrived but couldn’t be saved as a file. Please try again.');
      }
    } else {
      setMemoryNote(res.response || 'That didn’t work. Please try again in a moment.');
    }
    setMemoryBusy(false);
  };

  const deleteMemory = async () => {
    // A browser confirm is deliberate — deleting means gone, and this is the
    // one click on the page that cannot be walked back.
    if (!window.confirm('Delete everything Marlow keeps about you? This cannot be undone.')) return;
    setMemoryBusy(true);
    setMemoryNote(null);
    const res = await companionService.deleteAll();
    if (res?.success !== false) {
      clearThread(uid);
      setMessages([]);
      setRestoredCount(0);
      setSessionStarted(false);
      setUsage(null);
      dropModuleContext();
      setProfile(null);
      setShowMemory(false);
      setIntroDone(false);
    } else {
      setMemoryNote(res.response || 'That didn’t work. Please try again in a moment.');
    }
    setMemoryBusy(false);
  };

  const suppressUpgrade = Boolean(crisis) || professional?.tone === 'amber';

  if (authLoading || loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 flex items-center justify-center">
        <p className="text-slate-400">One moment…</p>
      </div>
    );
  }

  // Marlow is account-only: she keeps the story you were building and how the
  // telling went, and an anonymous session that evaporates with the browser
  // means the person did that work for nothing.
  //
  // But the intro comes FIRST, even signed out. The family's rule is that
  // nobody is asked for anything before they know who is asking and why — a
  // sign-in wall as the opening screen breaks it, and a visitor who has never
  // met Marlow has no reason to create an account for her.
  if (!canTalk) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 pt-32 pb-16 px-4 sm:px-6">
        {!introDone ? (
          <MarlowIntro onContinue={() => setIntroDone(true)} />
        ) : (
          <div className="max-w-xl mx-auto bg-slate-900 border border-white/10 rounded-2xl p-6 sm:p-8 text-center">
            <div className="flex justify-center mb-4">
              <MarlowAvatar size={48} />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-3">
              {staleLocalSession ? 'Please sign in again' : 'Marlow needs an account'}
            </h1>
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed mb-6">
              {staleLocalSession
                ? 'This device still has you signed in, but the session behind it has expired, so Marlow can’t reach your account. Signing in again will pick everything back up; nothing has been lost.'
                : 'Not for the sake of it: Marlow picks up where you left off, and remembers the story you were building so she can ask how the telling went. That only works if there’s an account to remember it against. You can delete everything she holds at any time.'}
            </p>
            <button
              onClick={() => redirectToAuth('signin')}
              className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg font-semibold transition-colors"
            >
              <LogIn size={18} /> {staleLocalSession ? 'Sign in again' : 'Sign in or create an account'}
            </button>
            <button
              onClick={() => setIntroDone(false)}
              className="block mx-auto mt-4 text-sm text-slate-400 hover:text-slate-100 transition-colors"
            >
              Back to who Marlow is
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!profile?.consent?.granted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 pt-32 pb-16 px-4 sm:px-6">
        {introDone
          ? <ConsentGate onAccept={handleGrantConsent} />
          : <MarlowIntro onContinue={() => setIntroDone(true)} />}
      </div>
    );
  }

  if (showIntro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 pt-32 pb-16 px-4 sm:px-6">
        <MarlowIntro
          returning
          hasThread={messages.length > 0}
          onContinue={() => setShowIntro(false)}
        />
      </div>
    );
  }

  // The composer only pins to the viewport once there is a conversation to
  // scroll — on an empty screen a fixed bar sits marooned at the bottom.
  //
  // NB this comment lives ABOVE the return on purpose: a {/* */} comment as a
  // sibling of the return root is the documented page-blanking trap in this
  // family of apps.
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 pt-24 md:pt-28 pb-28">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MarlowAvatar size={40} />
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-white">Marlow</h1>
              <p className="text-xs sm:text-sm text-slate-400">
                Bring the moment. Leave with the story.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={startNewConversation}
                className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                title="Start a new conversation"
                aria-label="Start a new conversation"
              >
                <RotateCcw size={18} />
              </button>
            )}
            <button
              onClick={() => setShowIntro(true)}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
              title="About Marlow"
              aria-label="About Marlow"
            >
              <HelpCircle size={18} />
            </button>
            <button
              onClick={() => { setShowMemory(!showMemory); setMemoryNote(null); }}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
              title="What Marlow keeps"
              aria-label="What Marlow keeps"
            >
              <SettingsIcon size={18} />
            </button>
          </div>
        </header>

        {showMemory && (
          <div className="mb-4 bg-slate-950 border border-white/10 rounded-xl p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-sm font-semibold text-amber-400">What Marlow keeps</h2>
              <button
                onClick={() => setShowMemory(false)}
                className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed mb-3">
              What you&apos;ve told Marlow about yourself, full transcripts of your conversations with
              her and summaries of them, and a one-line summary of your Storytelling Style Profile.
              It&apos;s kept for The Art of Story only, stored in Australia. Export a copy, or delete
              the lot; deleting means gone, and Marlow will meet you as a stranger next time.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={exportMemory}
                disabled={memoryBusy}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-slate-100 hover:bg-white/10 text-sm font-medium disabled:opacity-60 transition-colors"
              >
                <Download size={15} /> Export a copy
              </button>
              <button
                onClick={deleteMemory}
                disabled={memoryBusy}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-400/50 text-red-300 hover:bg-red-950/40 text-sm font-medium disabled:opacity-60 transition-colors"
              >
                <Trash2 size={15} /> Delete everything
              </button>
            </div>
            {memoryNote && <p className="text-xs sm:text-sm text-slate-400 mt-2">{memoryNote}</p>}
          </div>
        )}

        <div className="mb-3 space-y-2">
          <CompanionDisclaimer />
          <UsageIndicator usage={usage} suppressUpgrade={suppressUpgrade} />
          {fromModule && (
            <p className="text-xs sm:text-sm text-slate-300 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                Marlow knows you came from <span className="text-white">{fromModule.title}</span>
                {fromModule.section ? <>, on &ldquo;{fromModule.section}&rdquo;</> : null}.
              </span>
              <button
                type="button"
                onClick={dropModuleContext}
                className="text-amber-300 hover:text-white underline underline-offset-2"
              >
                Talk about something else
              </button>
            </p>
          )}
        </div>

        {messages.length === 0 && (
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-5 mb-4">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
              Hello {profile?.personalContext?.firstName || 'there'}.{' '}
              {fromModule
                ? `You've just been through ${fromModule.title}. Let's put it straight to work: tell me something that actually happened to you, and we'll find the story in it together.`
                : 'Tell me something that happened to you. It doesn’t have to be dramatic, and it doesn’t have to be finished. A moment you keep coming back to is plenty. Rambling is fine; rambling is where I do my best work.'}
            </p>
            <p className="text-xs sm:text-sm text-slate-400 mt-3">
              Bring a moment rather than a topic. Marlow works on the thing that actually happened,
              and has read every module in here.{' '}
              <button
                onClick={() => setShowIntro(true)}
                className="text-amber-300 hover:text-white underline underline-offset-2"
              >
                More about Marlow
              </button>
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(fromModule ? modulePrompts(fromModule.title) : suggestedPrompts).map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="text-left text-sm sm:text-base text-slate-200 bg-slate-950 hover:bg-amber-500/15 hover:text-amber-200 border border-white/10 rounded-lg p-3 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <AnimatePresence>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={i < restoredCount ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm sm:text-base leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-amber-500 text-slate-950 font-medium'
                      : m.isError
                        ? 'bg-red-950/60 text-red-100 border border-red-500/60'
                        : 'bg-slate-900 text-slate-100 border border-white/10'
                  }`}
                >
                  {m.role === 'assistant' && !m.isError ? renderMarkdown(m.content) : m.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {crisis && <CrisisResourcesCard resources={crisis} />}
          {!crisis && professional?.tone === 'amber' && <ProfessionalSupportCard support={professional} />}
          {!crisis && professional?.tone === 'subtle' && <SubtleProfessionalHint support={professional} />}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-slate-900 border border-white/10 rounded-2xl px-4 py-3 text-slate-400 text-sm">
                Marlow is listening…
              </div>
            </div>
          )}
          {/* Spacer so the newest message clears the fixed composer. */}
          <div ref={bottomRef} className="h-4" />
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="fixed bottom-0 left-0 right-0 bg-slate-950 border-t border-white/10 px-4 sm:px-6 py-3 z-40"
      >
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <label htmlFor="marlow-input" className="sr-only">Message Marlow</label>
          <textarea
            id="marlow-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            rows={1}
            placeholder="Something that happened to you…"
            className="flex-1 resize-none rounded-xl border border-white/15 bg-slate-900 text-slate-100 px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-amber-400/70 focus:border-amber-400/70 max-h-40 placeholder-slate-500"
            disabled={sending}
            maxLength={2000}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="flex-shrink-0 p-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl disabled:opacity-50 transition-colors"
            aria-label="Send"
          >
            <Send size={18} />
          </button>
        </div>
        {error && <p className="max-w-3xl mx-auto text-xs sm:text-sm text-red-300 mt-1">{error}</p>}
      </form>
    </div>
  );
}
